// ============================================================================
// assistantChat.ts — ЧАТ-FIRST ВХОД В ЛИЧНОГО АССИСТЕНТА (SSoT клиента).
//
// Механизм (БЕЗ второго мессенджера):
//   • один системный бот на базу (login='assistant_bot', агент system_assistant);
//   • у каждого пользователя — СВОЙ direct-чат с этим ботом (сервер создаёт
//     лениво при ListConversations; клиент лишь резолвит/создаёт при первом входе);
//   • сообщения идут штатным чатом (chatBridge/chatQueue — offline-first),
//     ответ готовит BotResponder с правами пользователя.
//
// Все входы (док, закреплённая строка, контекст «Спросить ассистента») используют
// ЭТИ функции — никаких параллельных каналов/дублей чата.
// ============================================================================

import { create } from 'zustand';
import type { AiBot } from '@zdc/sdk';
import { getSDK } from '@/store/sdkStore';
import { useAuthStore } from '@/store/authStore';
import { useCenterStore } from '@/store/centerStore';
import { createConversation, listConversations } from '@/lib/chatBridge';
import { sendChatMessage } from '@/lib/chatQueue';
import { openConversation } from '@/lib/chatNavigation';
import { getMyAssistantAvailability } from '@/lib/assistant/adapter';
import type { MyAssistantAvailability } from '@/lib/assistant/types';
import { friendlyError, logSwallowed } from '@/lib/errors';

/** Служебный логин системного бота (SSoT — metadata.AssistantBotLogin). */
export const ASSISTANT_BOT_LOGIN = 'assistant_bot';
/** Код системного агента (SSoT — metadata.SystemAgentCode). */
export const ASSISTANT_AGENT_CODE = 'system_assistant';

export interface AssistantBotRef {
  id: string;
  name: string;
  avatarUrl?: string;
}

/** Минимальный вид диалога для резолва личного чата с ассистентом. */
export interface AssistantConversationLike {
  id: string;
  kind?: string;
  title?: string;
  memberIds?: string[];
}

/**
 * Состояние ЧАТА АССИСТЕНТА для оболочки (док) и списка чатов:
 *   • activeId — открыт личный чат ассистента (подсветка входа в доке);
 *   • opening — идёт резолв/создание чата (кнопки не дублируют запрос);
 *   • error — последняя ошибка входа (показывается у закреплённой строки).
 * Одна точка состояния: и док, и список чатов читают одно и то же.
 */
interface AssistantChatState {
  activeId: string;
  opening: boolean;
  error: string | null;
  // Доступность закреплённого чата: нет шаблона/привязки/модели/провайдера —
  // вход не показывается (fail-closed, причина доступна из state).
  availability: MyAssistantAvailability | null;
  availabilityBase: string;
  availabilityLoading: boolean;
  setActive: (id: string) => void;
  beginOpening: () => void;
  endOpening: () => void;
  setError: (message: string | null) => void;
}

export const useAssistantChatStore = create<AssistantChatState>((set) => ({
  activeId: '',
  opening: false,
  error: null,
  availability: null,
  availabilityBase: '',
  availabilityLoading: false,
  setActive: (id) => set({ activeId: id || '' }),
  beginOpening: () => set({ opening: true, error: null }),
  endOpening: () => set({ opening: false }),
  setError: (message) => set({ error: message }),
}));

/** Сбросить состояние входа в ассистента (смена базы/выход). */
export function resetAssistantChat(): void {
  useAssistantChatStore.setState({
    activeId: '',
    opening: false,
    error: null,
    availability: null,
    availabilityBase: '',
    availabilityLoading: false,
  });
}

// Доступность — один запрос на базу (док и список чатов не дублируют вызов).
let availabilityInFlight: Promise<MyAssistantAvailability> | null = null;
let availabilityInFlightBase = '';
// Номер последнего запроса: ответ устаревшего запроса не перетирает новый.
let availabilitySeq = 0;

function unavailable(reason: string): MyAssistantAvailability {
  return {
    available: false,
    reason,
    source: 'none',
    templateCode: '',
    hasBinding: false,
    agentCode: '',
    modelId: '',
    providerId: '',
  };
}

/**
 * Перечитать доступность ассистента для текущего пользователя. force=true —
 * после смены политики базы (админ) или явного запроса.
 */
export async function refreshAssistantAvailability(force = false): Promise<MyAssistantAvailability> {
  const base = useCenterStore.getState().baseSlug || '';
  const state = useAssistantChatStore.getState();
  if (!force && state.availability && state.availabilityBase === base) {
    return state.availability;
  }
  if (availabilityInFlight && availabilityInFlightBase === base) {
    return availabilityInFlight;
  }
  availabilityInFlightBase = base;
  const seq = ++availabilitySeq;
  useAssistantChatStore.setState({ availabilityLoading: true });
  const request = (async () => {
    try {
      const result = await getMyAssistantAvailability();
      if (availabilitySeq === seq) {
        useAssistantChatStore.setState({ availability: result, availabilityBase: base });
      }
      return result;
    } catch (e) {
      logSwallowed(e, 'lib/assistantChat.ts:availability');
      const result = unavailable(friendlyError(e));
      // Ошибку НЕ кэшируем как состояние базы: следующий вызов повторит
      // проверку (сеть восстановилась/настройка изменилась).
      if (availabilitySeq === seq) {
        useAssistantChatStore.setState({ availability: result, availabilityBase: '' });
      }
      return result;
    } finally {
      if (availabilitySeq === seq) {
        availabilityInFlight = null;
        useAssistantChatStore.setState({ availabilityLoading: false });
      }
    }
  })();
  availabilityInFlight = request;
  return request;
}

// Резолв/создание чата — один на все входы (док + закреплённая строка):
// повторные клики НЕ создают второй direct-чат и не дублируют запросы.
let ensureInFlight: Promise<{ conversationId: string; bot: AssistantBotRef }> | null = null;

/** Системный бот ассистента: по служебному логину, затем по системному агенту. */
export async function findAssistantBot(): Promise<AssistantBotRef | null> {
  const res: any = await getSDK().ai.listAiBots({});
  const items: AiBot[] = (res?.items || []) as AiBot[];
  // Приоритет — СЛУЖЕБНЫЙ логин системного бота; затем системный бот с нужным
  // агентом (если логин переименован); fallback по агенту — для совместимости.
  const bot = items.find((item) => item.login === ASSISTANT_BOT_LOGIN)
    || items.find((item) => item.isSystem && item.agentCode === ASSISTANT_AGENT_CODE)
    || items.find((item) => item.agentCode === ASSISTANT_AGENT_CODE);
  if (!bot?.id) return null;
  return {
    id: String(bot.id),
    name: String(bot.name || 'Мой ассистент'),
    avatarUrl: bot.avatarUrl || '',
  };
}

/**
 * Признак «это чат с ассистентом» — ЕДИНЫЙ предикат (SSoT): direct-чат, среди
 * участников которого есть бот-ассистент. Нужен и для резолва закреплённой
 * строки, и для исключения ВСЕХ таких чатов из общего списка (легаси-дубли,
 * созданные до серверной идемпотентности, тоже считаются чатом ассистента).
 */
export function isAssistantConversation(
  conversation: AssistantConversationLike,
  assistantBotId: string,
): boolean {
  if (!assistantBotId) return false;
  return conversation.kind === 'direct'
    && Array.isArray(conversation.memberIds)
    && conversation.memberIds.includes(assistantBotId);
}

/** Direct-чат пользователя с ассистентом (если уже есть в списке). */
export function findAssistantConversation(
  conversations: AssistantConversationLike[],
  assistantBotId: string,
): AssistantConversationLike | null {
  if (!assistantBotId) return null;
  return conversations.find((c) => isAssistantConversation(c, assistantBotId)) || null;
}

/**
 * Получить/создать ЛИЧНЫЙ чат с ассистентом. Бот должен существовать (ядро
 * создаёт его при bootstrap базы); его отсутствие — явная ошибка, не заглушка.
 */
export async function ensureAssistantConversation(): Promise<{ conversationId: string; bot: AssistantBotRef }> {
  if (ensureInFlight) return ensureInFlight;
  ensureInFlight = resolveAssistantConversation();
  try {
    return await ensureInFlight;
  } finally {
    ensureInFlight = null;
  }
}

async function resolveAssistantConversation(): Promise<{ conversationId: string; bot: AssistantBotRef }> {
  const bot = await findAssistantBot();
  if (!bot) {
    throw new Error('В базе нет системного бота «Мой ассистент». Обратитесь к администратору.');
  }
  const conversations = (await listConversations()) as AssistantConversationLike[];
  const existing = findAssistantConversation(conversations, bot.id);
  if (existing) return { conversationId: String(existing.id), bot };
  const created = await createConversation({ kind: 'direct', memberIds: [bot.id] });
  return { conversationId: created.id, bot };
}

/** Открыть личный чат ассистента (док/закреплённая строка): создать при первом входе. */
export async function openAssistantChat(): Promise<string> {
  const availability = await refreshAssistantAvailability();
  if (!availability.available) {
    throw new Error(availability.reason || 'Ассистент недоступен: не настроен шаблон или модель.');
  }
  const ui = useAssistantChatStore.getState();
  // Уже открыт — только подсветить (без повторного резолва и перезагрузки).
  if (ui.activeId) {
    openConversation(ui.activeId);
    return ui.activeId;
  }
  ui.beginOpening();
  try {
    const { conversationId } = await ensureAssistantConversation();
    useAssistantChatStore.getState().setActive(conversationId);
    openConversation(conversationId);
    return conversationId;
  } catch (e) {
    useAssistantChatStore.getState().setError(e instanceof Error ? e.message : String(e));
    throw e;
  } finally {
    useAssistantChatStore.getState().endOpening();
  }
}

/** Отправить сообщение личному ассистенту (штатный offline-first чат). */
export async function sendToAssistant(text: string): Promise<{ conversationId: string; queued: boolean }> {
  const prompt = text.trim();
  if (!prompt) throw new Error('Пустой запрос ассистенту.');
  const availability = await refreshAssistantAvailability();
  if (!availability.available) {
    throw new Error(availability.reason || 'Ассистент недоступен: не настроен шаблон или модель.');
  }
  const myId = String(useAuthStore.getState().user?.id || '');
  if (!myId) throw new Error('Не удалось определить текущего пользователя.');
  const { conversationId } = await ensureAssistantConversation();
  const result = await sendChatMessage({
    conversationId,
    text: prompt,
    attachments: [],
    replyTo: '',
  }, myId);
  return { conversationId, queued: result.queued };
}
